import {
  CartesianGrid,
  Legend,
  Line,
  LineChart as RLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { chartSeriesToken } from '../ChartSeriesTokens'
import { useWidgetDensity } from '../grid/WidgetDensityContext'
import { Sparkline } from './Sparkline'
import { ChartAccessibleTable, ChartFrame } from './ChartFrame'

export interface LineSeriesDef {
  key: string
  label: string
}

export interface LineChartWrapperProps<T extends object> {
  title: string
  data: T[]
  categoryKey: string
  categoryLabel: string
  series: LineSeriesDef[]
  valueFormatter?: (value: number) => string
  height?: number
}

/**
 * AC5 — QRE / qualified-spend trend. Lines can overlap, so every series gets its own dash pattern
 * once >1 series is shown.
 *
 * **Auto-density (IB-311 Slice 1):** `compact` drops gridlines/legend/axis-tick text; `micro`
 * collapses the primary series to a `Sparkline` + total, same posture as `BarChartWrapper`.
 */
export function LineChartWrapper<T extends object>({
  title,
  data,
  categoryKey,
  categoryLabel,
  series,
  valueFormatter = (v) => String(v),
  height = 260,
}: LineChartWrapperProps<T>) {
  const { density } = useWidgetDensity()
  const asRecord = (row: T) => row as unknown as Record<string, unknown>
  const primaryKey = series[0]?.key

  const table = (
    <ChartAccessibleTable
      caption={`${title} — data table`}
      columns={[
        { key: 'category', label: categoryLabel },
        ...series.map((s) => ({ key: s.key, label: s.label, align: 'end' as const })),
      ]}
      rows={data.map((row) => {
        const out: Record<string, string> = { category: String(asRecord(row)[categoryKey] ?? '') }
        for (const s of series) out[s.key] = valueFormatter(Number(asRecord(row)[s.key] ?? 0))
        return out
      })}
    />
  )

  const microFallback =
    primaryKey && data.length > 0 ? (
      <Sparkline
        points={data.map((row) => ({
          key: String(asRecord(row)[categoryKey] ?? ''),
          label: String(asRecord(row)[categoryKey] ?? ''),
          value: Number(asRecord(row)[primaryKey] ?? 0),
        }))}
        valueFormatter={valueFormatter}
        summaryLabel={`Latest: ${data.length > 0 ? valueFormatter(Number(asRecord(data[data.length - 1]!)[primaryKey] ?? 0)) : '—'}`}
      />
    ) : undefined

  return (
    <ChartFrame
      title={title}
      height={height}
      table={table}
      microFallback={microFallback}
      chart={
        <ResponsiveContainer width="100%" height="100%">
          <RLineChart data={data}>
            {density === 'full' ? <CartesianGrid strokeDasharray="3 3" stroke="var(--iw-border)" /> : null}
            <XAxis
              dataKey={categoryKey}
              tick={density === 'full' ? { fill: 'var(--iw-text-muted)', fontSize: 12 } : false}
              tickLine={density === 'full'}
              axisLine={density !== 'micro'}
            />
            <YAxis
              tick={density === 'full' ? { fill: 'var(--iw-text-muted)', fontSize: 12 } : false}
              tickFormatter={valueFormatter}
              width={density === 'full' ? 60 : 0}
              axisLine={density !== 'micro'}
              tickLine={density === 'full'}
            />
            <Tooltip formatter={(value) => valueFormatter(Number(value))} />
            {series.length > 1 && density === 'full' ? <Legend /> : null}
            {series.map((s, i) => {
              const token = chartSeriesToken(i)
              const dashProps = series.length > 1 && token.dash ? { strokeDasharray: token.dash } : {}
              return (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={token.color}
                  strokeWidth={2}
                  dot={density === 'full' ? { r: 3 } : false}
                  {...dashProps}
                />
              )
            })}
          </RLineChart>
        </ResponsiveContainer>
      }
    />
  )
}
